import React, { useEffect, useRef, useState } from 'react'
import { getOptimizedUrl } from '@disco/api/imgixClient'
import AspectRatio from 'components/ui/aspect-ratio'
import styles from './index.module.css'

export const PLACEHOLDER: React.ReactNode = (
  <svg viewBox="0 0 100 100">
    <g
      fill="none"
      stroke="currentcolor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M55.403 52.191L40.465 63.615l-4.31-4.31 11.426-14.942M34 63.615l2.155 2.155" />
      <path d="M63.092 49.608a9.143 9.143 0 10-12.93-12.93 9.143 9.143 0 0012.93 12.93z" />
    </g>
  </svg>
)
type Props = {
  color?: string | null | undefined
  overlay?: React.ReactNode
  status?: React.ReactNode
  image?: string | null | undefined
  imageParams?: Record<string, any>
}

/**
 * Artist artwork component with circular display and optimized image loading
 * @param color - Background color for the artwork
 * @param overlay - Optional overlay content to display on top
 * @param status - Optional status indicator to show below the artwork
 * @param image - URL of the artist artwork image
 * @param imageParams - Parameters for image optimization
 */
const ArtistArtwork = ({
  color,
  overlay,
  status,
  image,
  imageParams,
}: Props): React.ReactElement<'div'> => {
  const artistArtworkRef = useRef(null)
  const [buildURL, setBuildURL] = useState(null)
  useEffect(() => {
    if (!image) {
      setBuildURL(null)
      return
    }

    const result = getOptimizedUrl({
      imageContainer: artistArtworkRef.current,
      image,
      params: imageParams,
    })
    // Fallback to original image if optimization fails
    setBuildURL(result || image)
  }, [image, imageParams, artistArtworkRef.current])
  return (
    <div className={styles.artistArtwork} ref={artistArtworkRef}>
      <AspectRatio fill={color} image={buildURL} ratio={1} shape="circle">
        {!buildURL && PLACEHOLDER}
        {overlay}
      </AspectRatio>
      {status && <div className={styles.artistArtworkStatus}>{status}</div>}
    </div>
  )
}

export default ArtistArtwork
